/**
 * React Query hooks for masters management
 * Requirements: 16.1, 16.4, 16.5
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchMasters, 
  approveMaster, 
  rejectMaster,
  blockMaster,
  unblockMaster,
  type MasterStatus,
  type PaginatedResponse,
  type Master,
} from '@/lib/api';

// Query keys
export const masterKeys = {
  all: ['masters'] as const,
  lists: () => [...masterKeys.all, 'list'] as const,
  list: (filters: {
    status?: MasterStatus;
    activeApproved?: boolean;
    onlyMyApproved?: boolean;
    page?: number;
  }) => [...masterKeys.lists(), filters] as const,
  detail: (id: string) => [...masterKeys.all, 'detail', id] as const,
};

/**
 * Hook to fetch masters with optional filters
 */
export function useMasters(params: {
  status?: MasterStatus;
  activeApproved?: boolean;
  onlyMyApproved?: boolean;
  page?: number;
  limit?: number;
  enabled?: boolean;
}) {
  return useQuery<PaginatedResponse<Master>, Error>({
    queryKey: masterKeys.list({
      status: params.status,
      activeApproved: params.activeApproved,
      onlyMyApproved: params.onlyMyApproved,
      page: params.page,
    }),
    queryFn: () => fetchMasters({
      status: params.status,
      activeApproved: params.activeApproved,
      onlyMyApproved: params.onlyMyApproved,
      page: params.page,
      limit: params.limit,
    }),
    enabled: params.enabled !== false,
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook to fetch all masters (no status filter)
 */
export function useAllMasters(params: {
  page?: number;
  limit?: number;
  enabled?: boolean;
}) {
  return useQuery<PaginatedResponse<Master>, Error>({
    queryKey: masterKeys.list({ page: params.page }),
    queryFn: () => fetchMasters({
      page: params.page,
      limit: params.limit,
    }),
    enabled: params.enabled !== false,
    staleTime: 30_000,
  });
}

/**
 * Hook to approve a master
 */
export function useApproveMaster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (masterId: string) => approveMaster(masterId),
    onSuccess: () => {
      // Invalidate all master lists to refresh data
      queryClient.invalidateQueries({ queryKey: masterKeys.lists() });
    },
  });
}

/**
 * Hook to reject a master
 */
export function useRejectMaster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ masterId, reason }: { masterId: string; reason: string }) => 
      rejectMaster(masterId, reason),
    onSuccess: () => {
      // Invalidate all master lists to refresh data
      queryClient.invalidateQueries({ queryKey: masterKeys.lists() });
    },
  });
}

/**
 * Hook to block a master
 * Requirements: 16.5
 */
export function useBlockMaster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ masterId, reason }: { masterId: string; reason: string }) => 
      blockMaster(masterId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: masterKeys.lists() });
    },
  });
}

/**
 * Hook to unblock a master
 * Requirements: 16.5
 */
export function useUnblockMaster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (masterId: string) => unblockMaster(masterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: masterKeys.lists() });
    },
  });
}
